import { Contract, ContractArchiveDocument, ContractAttachment, DocumentType } from "@/types";
import { Grid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { EditContractInput } from "../../validation/editContract";
import { Controller, UseFormReturn } from "react-hook-form";
import Textarea from "@/components/atoms/Textarea";
import DocumentList from "@/components/molecules/DocumentList";
import Dropzone from "@/components/molecules/Dropzone";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createContractAttachment,
  createContractImage,
  deleteContractAttachment,
  deleteContractImage,
} from "@/services/Contracts";
import { FileWithPath } from "@mantine/dropzone";
import { fileToBase64Raw, getFileTypeFromFile } from "@/utils/FileUtils";
import { ErrorNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";
import { Dispatch, SetStateAction } from "react";

interface AttachmentsProps {
  editContractForm: UseFormReturn<EditContractInput>;
  contract: Contract;
  disableForm: boolean;
  files: ContractAttachment[];
  setFiles: Dispatch<SetStateAction<ContractAttachment[]>>;
}

const Attachments = ({ files, setFiles, editContractForm, contract, disableForm }: AttachmentsProps) => {
  const { t } = useTranslation();

  const { currentCompany } = useAuth();
  const queryClient = useQueryClient();
  console.log(files);
  const { mutateAsync: mutateDeleteAttachment } = useMutation({
    mutationFn: async (attachmentId: string) => {
      await deleteContractAttachment(contract.id, [attachmentId]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractAttachments", contract.id],
      });
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic:deleteError"), t("document.deleteError")));
    },
  });

  const { mutateAsync: mutateDeleteImage } = useMutation({
    mutationFn: async (imageId: string) => {
      await deleteContractImage(contract.id, [imageId]);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractAttachments", contract.id],
      });
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic:deleteError"), t("document.deleteError")));
    },
  });

  const { mutateAsync: mutateUploadImages, isPending: isPendingImages } = useMutation({
    mutationFn: async (newFiles: File[]) => {
      const contractDocuments: ContractArchiveDocument[] = await Promise.all(
        newFiles.map(async (file) => {
          const base64File = await fileToBase64Raw(file as File);
          return {
            documentType: "IMAGE" as DocumentType,
            fileType: getFileTypeFromFile(file as File),
            document: base64File,
            documentTitle: file.name,
            languageId: currentCompany?.companyLanguageId as number,
            contractId: contract.id,
            companyId: currentCompany?.companyId,
          };
        })
      );
      const res = await createContractImage(contract.id, contractDocuments);
      return res.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractAttachments", contract.id],
      });
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic:createError"), t("document.uploadError")));
    },
  });

  const { mutateAsync: mutateUploadAttachments, isPending: isPendingAttachments } = useMutation({
    mutationFn: async (documents: File[]) => {
      const fileTypes = documents.map((document) => getFileTypeFromFile(document as File));
      return await createContractAttachment(contract.id, documents, fileTypes);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getContractAttachments", contract.id],
      });
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic:createError"), t("document.uploadError")));
    },
  });

  const handleAdd = async (files: FileWithPath[]) => {
    const images = files.filter((file) => file.type.split("/")[0] === "image");
    const attachments = files.filter((file) => file.type.split("/")[0] === "application");

    if (images.length > 0 && attachments.length > 0) {
      await mutateUploadImages(images);
      await mutateUploadAttachments(attachments);
    } else if (attachments.length > 0) {
      await mutateUploadAttachments(attachments);
    } else if (images.length > 0) {
      await mutateUploadImages(images);
    } else {
      return;
    }
  };

  const handleRemove = async (fileId: string) => {
    const fileToRemove = files.find((file) => file.id === fileId);
    if (!fileToRemove) return;
    if (fileToRemove.documentType === "IMAGE") {
      await mutateDeleteImage(fileToRemove.id);
    } else {
      await mutateDeleteAttachment(fileToRemove.id);
    }
  };

  return (
    <>
      <Grid>
        {contract.createdByCompany === currentCompany?.companyId ? (
          <Grid.Col span={{ base: 12, lg: 6 }}>
            <Dropzone
              isLoading={isPendingImages || isPendingAttachments}
              origin="api"
              type={"dropzone"}
              files={files}
              showAsImageCarousel={false}
              handleAdd={handleAdd}
              handleDelete={handleRemove}
              disabled={contract.createdByCompany !== currentCompany?.companyId}
            />
          </Grid.Col>
        ) : (
          <Grid.Col span={{ base: 12, xl: 6 }}>
            <DocumentList
              origin={"api"}
              files={files}
              onRemoveFile={handleRemove}
              disabled={contract.createdByCompany !== currentCompany?.companyId}
            />
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Controller
            control={editContractForm.control}
            name="comment"
            render={({ field: { onChange, value } }) => (
              <Textarea
                id="edit-contract-comment"
                data-testid="edit-contract-comment"
                label={t("contracts:moreComments")}
                disabled={disableForm}
                onChange={onChange}
                value={value}
              />
            )}
          />
        </Grid.Col>
      </Grid>
    </>
  );
};

export default Attachments;
